import { Loader } from '@talon/web/src/components/ui';
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ProjectRoute, RequireAuth } from './components';

const Home = lazy(() => import('@talon/web/src/pages/home'));
const PerformerHome = lazy(() => import('@talon/web/src/pages/performer-home'));
const Login = lazy(() => import('@talon/web/src/pages/login'));
const Notifications = lazy(() => import('@talon/web/src/pages/notifications'));
const SessionEstimator = lazy(() => import('@talon/web/src/pages/session-estimator'));
const UsageEstimator = lazy(() => import('@talon/web/src/pages/usage-estimator'));
const HireTalent = lazy(() => import('@talon/web/src/pages/hire-talent'));
const TimeTracking = lazy(() => import('@talon/web/src/pages/time-tracking'));
const Estimate = lazy(() => import('@talon/web/src/pages/estimate'));
const Resources = lazy(() => import('@talon/web/src/pages/resources'));
const Performers = lazy(() => import('@talon/web/src/pages/performers'));
const PerformerDashboard = lazy(() => import('@talon/web/src/pages/PerformerDashboard'));
const Profile = lazy(() => import('@talon/web/src/pages/profile'));
const Account = lazy(() => import('@talon/web/src/pages/account'));
const ArchivedProjects = lazy(() => import('@talon/web/src/pages/archived-projects'));
const NotFound = lazy(() => import('@talon/web/src/pages/not-found'));
const Storage = lazy(() => import('@talon/web/src/pages/storage'));
const ProjectHome = lazy(() => import('./pages/project-home'));
const ResetPassword = lazy(() => import('./pages/reset-password'));
const Signup = lazy(() => import('./pages/signup'));
const SignupReturn = lazy(() => import('./pages/signup-return'));
const Admin = lazy(() => import('./pages/admin'));
const SupportInboxPage = lazy(() => import('./pages/support-inbox'));
const ConfirmEmail = lazy(() => import('./pages/confirm-email'));
const OnboardingFlow = lazy(() => import('./pages/performer-onboarding'));
const CharacterCaptureMobile = lazy(() => import('./pages/character-capture-mobile'));
const Payroll = lazy(() => import('./pages/payroll'));
const Edits = lazy(() => import('./pages/edits'));
const OAuthPopupClose = lazy(() => import('./pages/oauth-popup-close'));
const LicensingRights = lazy(() => import('./pages/licensing-rights'));
const HoldingFees = lazy(() => import('./pages/holding-fees'));
const PaySessions = lazy(() => import('./pages/pay-sessions'));
const AssetLibrary = lazy(() => import('./pages/AssetLibrary'));
const AssetDashboard = lazy(() => import('./pages/AssetDashboard'));
const Marketplace = lazy(() => import('./pages/marketplace'));
const MarketplaceProfile = lazy(() => import('./pages/marketplace-profile'));
const TmDashboard = lazy(() => import('./pages/tm-dashboard'));
const ProjectTalentManager = lazy(() => import('./pages/project-talent-manager'));
const TmSignup = lazy(() => import('./pages/tm-signup'));

export const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      {/* prettier-ignore */}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/tm-signup" element={<TmSignup />} />
        <Route path="/signup/return" element={<SignupReturn />} />
        <Route path="/group-invitation" element={<Signup />} />
        <Route path="/confirm" element={<ConfirmEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding/:token" element={<OnboardingFlow />} />
        <Route path="/character-capture/:token" element={<CharacterCaptureMobile />} />
        <Route path="/oauth-popup-close" element={<OAuthPopupClose />} />
        <Route path="/resources" element={<RequireAuth><Resources /></RequireAuth>}/>
        <Route path="/performers" element={<RequireAuth><Performers /></RequireAuth>}/>
        <Route path="/performers/:performerId" element={<RequireAuth><PerformerDashboard /></RequireAuth>}/>
        <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>}/>
        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>}/>
        <Route path="/archived" element={<RequireAuth><ArchivedProjects /></RequireAuth>}/>
        <Route path="/storage" element={<RequireAuth><Storage /></RequireAuth>}/>
        <Route path="/admin/:section?" element={<RequireAuth adminOnly><Admin /></RequireAuth>}/>
        <Route path="/support-inbox" element={<RequireAuth supportAgentOnly><SupportInboxPage /></RequireAuth>}/>
        <Route path="/" element={<RequireAuth><Home /></RequireAuth>}/>

        {/* Project Routes */}
        <Route path="/project/:projectName">

          {/* Pre pro */}
          <Route path="" element={<RequireAuth><ProjectRoute><ProjectHome /></ProjectRoute></RequireAuth>} />
          <Route path="estimate" element={<RequireAuth><ProjectRoute><Estimate /></ProjectRoute></RequireAuth>}>
            <Route path=":estimateId" element={<RequireAuth><ProjectRoute><Estimate /></ProjectRoute></RequireAuth>} />
          </Route>
          <Route path="session-estimator" element={<RequireAuth><ProjectRoute><SessionEstimator /></ProjectRoute></RequireAuth>}/>
          <Route path="usage-estimator" element={<RequireAuth><ProjectRoute><UsageEstimator /></ProjectRoute></RequireAuth>}/>

          {/* Production */}
          <Route path="licensing-rights" element={<RequireAuth><ProjectRoute><LicensingRights /></ProjectRoute></RequireAuth>}/>
          <Route path="holding-fees" element={<RequireAuth><ProjectRoute><HoldingFees /></ProjectRoute></RequireAuth>}/>
          <Route path="hire-talent" element={<RequireAuth><ProjectRoute><HireTalent /></ProjectRoute></RequireAuth>}/>
          <Route path="hire-talent/:contractId" element={<RequireAuth><ProjectRoute><HireTalent /></ProjectRoute></RequireAuth>}/>
          <Route path="time-tracking" element={<RequireAuth><ProjectRoute><TimeTracking /></ProjectRoute></RequireAuth>}/>
          <Route path="pay-sessions" element={<RequireAuth><ProjectRoute><PaySessions /></ProjectRoute></RequireAuth>}/>
          
          {/* Post */}
          <Route path="edits" element={<RequireAuth><ProjectRoute><Edits /></ProjectRoute></RequireAuth>}/>
          <Route path="edits/:commercialId" element={<RequireAuth><ProjectRoute><Edits /></ProjectRoute></RequireAuth>}/>
          <Route path="edits/:commercialId/:editId" element={<RequireAuth><ProjectRoute><Edits /></ProjectRoute></RequireAuth>}/>
          <Route path="asset-library" element={<RequireAuth><ProjectRoute><AssetLibrary /></ProjectRoute></RequireAuth>}/>
          <Route path="asset-library/:assetId" element={<RequireAuth><ProjectRoute><AssetDashboard /></ProjectRoute></RequireAuth>}/>
          <Route path="talent-manager" element={<RequireAuth><ProjectRoute><ProjectTalentManager /></ProjectRoute></RequireAuth>}/>
        </Route>

        <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>}/>
        <Route path="/performer-home" element={<RequireAuth><PerformerHome /></RequireAuth>}/>
        <Route path="/payroll" element={<RequireAuth><Payroll /></RequireAuth>}/>

        {/* Talent Manager Marketplace */}
        <Route path="/marketplace" element={<RequireAuth><Marketplace /></RequireAuth>}/>
        <Route path="/marketplace/:id" element={<RequireAuth><MarketplaceProfile /></RequireAuth>}/>
        <Route path="/talent-manager" element={<RequireAuth><TmDashboard /></RequireAuth>}/>
        <Route path="*" element={<RequireAuth><NotFound /></RequireAuth>}/>
      </Routes>
    </Suspense>
  );
};
